import * as Yup from 'yup';
import { useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, RHFRadioGroup, useAuthContext, RHFAutocompleteSelect } from '@emfood/shared';

import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Box, Card, Typography, CircularProgress } from '@mui/material';

import { GenerationHistoryItemPreviewProps } from 'src/api/models/generation-history';
import {
  useGenerateStory,
  useGetProducersByBuyerSite,
  useGetProductTemplatesByProducer,
} from 'src/api/hooks/api-hooks';

interface DialogProps {
  open: boolean;
  onClose: any;
  onOpenPreview: (story: GenerationHistoryItemPreviewProps) => void;
}

interface GenerateStoryFormData {
  producerId: string;
  productTemplateId: string;
  storyLength: 'short' | 'medium' | 'long';
}

export default function GenerateNewStoryDialog({ open, onClose, onOpenPreview }: DialogProps) {
  const { user } = useAuthContext();
  const buyerOrgId = user?.buyerOrg?.id || 'a49199a3-8448-4148-b82b-73fe5ed1e083';
  const buyerSiteId = user?.buyerSite?.id || '1d748aa5-ce4a-4b26-8eb4-b4eb2dce4fbe';

  const GenerateStorySchema = Yup.object().shape({
    producerId: Yup.string().required('Producer is required'),
    productTemplateId: Yup.string().required('Product template is required'),
    storyLength: Yup.string()
      .oneOf(['short', 'medium', 'long'], 'Story length is required')
      .required('Story length is required'),
  });

  const defaultValues: GenerateStoryFormData = useMemo(
    () => ({
      producerId: '',
      productTemplateId: '',
      storyLength: 'short',
    }),
    []
  );

  const methods = useForm<GenerateStoryFormData>({
    resolver: yupResolver(GenerateStorySchema),
    defaultValues,
  });

  const {
    handleSubmit: handleStorySubmit,
    formState: { isSubmitting, isValid },
    reset,
    watch,
  } = methods;

  const selectedProducerId = watch('producerId');

  // Fetch producers data
  const { data: producersResponse, isLoading: isLoadingProducers } = useGetProducersByBuyerSite(
    buyerOrgId,
    buyerSiteId
  );

  // Fetch product templates data (only when producer is selected)
  const { data: productTemplatesResponse, isLoading: isLoadingTemplates } =
    useGetProductTemplatesByProducer(selectedProducerId || null);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: generateStory, isLoading: isGeneratingStory } = useGenerateStory();

  const onSubmitGenerateStory = handleStorySubmit((formData) => {
    generateStory(
      {
        body: {
          productId: formData.productTemplateId,
          storyLength: formData.storyLength,
        },
      },
      {
        onSuccess: (response) => {
          enqueueSnackbar('Story generated successfully!', { variant: 'success' });
          queryClient.invalidateQueries(['generation-history']);

          // Create a story object for the preview dialog
          const generatedStory: GenerationHistoryItemPreviewProps = {
            content: response?.story || response?.content || 'Story content not available',
            productId: formData.productTemplateId,
          };

          onClose();
          reset();
          onOpenPreview(generatedStory);
        },
        onError: (error: any) => {
          enqueueSnackbar(
            error?.response?.data?.message || 'Failed to generate story. Please try again.',
            { variant: 'error' }
          );
        },
      }
    );
  });

  const storyLengthOptions = [
    { value: 'short', label: '1-2 sentences' },
    { value: 'medium', label: '2 paragraphs' },
    { value: 'long', label: 'Half to 3/4 of a page' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmitGenerateStory}>
        <DialogTitle>Generate New Story</DialogTitle>

        <DialogContent>
          {isGeneratingStory ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={8}
              gap={3}
            >
              <CircularProgress size={60} />
              <Typography variant="h6" textAlign="center">
                Generating your story...
              </Typography>
              <Typography
                variant="body2"
                textAlign="center"
                color="text.secondary"
                sx={{ maxWidth: 400 }}
              >
                This may take a few moments. Please be patient while we create your personalized
                content.
              </Typography>
            </Box>
          ) : (
            <Card sx={{ p: 3 }}>
              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: 'repeat(1, 1fr)',
                }}
              >
                <RHFAutocompleteSelect
                  name="producerId"
                  label="Producer *"
                  placeholder="Select a producer"
                  options={
                    producersResponse?.data?.map((producer) => ({
                      id: producer.id,
                      name: producer.name,
                    })) || []
                  }
                  loading={isLoadingProducers}
                  disabled={isGeneratingStory}
                />

                <RHFAutocompleteSelect
                  name="productTemplateId"
                  label="Product Template *"
                  placeholder="Select a product template"
                  options={
                    productTemplatesResponse?.map((template) => ({
                      id: template.id,
                      name: template.name,
                    })) || []
                  }
                  loading={isLoadingTemplates}
                  disabled={!selectedProducerId || isGeneratingStory}
                />

                <RHFRadioGroup
                  name="storyLength"
                  label="Story Length *"
                  options={storyLengthOptions}
                />
              </Box>
            </Card>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="inherit" disabled={isGeneratingStory}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting || isGeneratingStory}
            disabled={!isValid || isGeneratingStory}
          >
            Generate
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
